import AbstractFormulaVisitor from "./AbstractFormulaVisitor.js";

/**
 * Visitor used to check if a key figure has a specific dependency.
 *
 * @param {string} dependencyId - Unique identifier of the dependency
 * we are trying to find in the key figure
 */
export default class KeyFigureDependencyChecker extends AbstractFormulaVisitor {
	constructor({ dependencyId }) {
		super(...arguments);

		this._dependencyId = dependencyId;
	}

	/**
	 * Recursively check if the keyFigure references the
	 * dependencyId. Returns null if the keyFigure formula references
	 * a non-existing keyFigure.
	 */
	hasDependency({ keyFigure }) {
		if (keyFigure.getId() === this._dependencyId) {
			return true;
		}

		const formula = this._getKeyFigureFormula(keyFigure);

		return formula ? this.visit(formula) : null;
	}

	visitAbstractNode(_node) {
		return false;
	}

	visitBinaryOperatorNode(node) {
		return (
			node.getLeftOperand().accept(this) ||
			node.getRightOperand().accept(this)
		);
	}

	visitKeyFigureNode(node) {
		return this.hasDependency({ keyFigure: node.getKeyFigure() });
	}

	visitWrapperNode(node) {
		return node.getExpression().accept(this);
	}

	/**
	 * Helper method to get the keyFigure formula. If a keyFigure
	 * references another keyFigure that doesn't exist
	 * anymore, the visitor would crash.
	 */
	_getKeyFigureFormula(keyFigure) {
		try {
			return keyFigure.getFormula();
		} catch {
			return null;
		}
	}

	/**
	 * Checks if the keyFigure references - directly or indirectly -
	 * the dependencyId. It returns an object containing two values:
	 * `hasDependency` makes sense only if `isFormulaValid` is true.
	 */
	static checkDependency({ keyFigure, dependencyId }) {
		const hasDependency = new this({ dependencyId }).hasDependency({
			keyFigure,
		});

		return { isFormulaValid: hasDependency !== null, hasDependency };
	}
}
